import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

export default function TermsPage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-viraly-dark">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-0 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
            <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-viraly-muted hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-viraly-orange" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold">Terms of Service</h1>
                <p className="text-viraly-muted">Last Updated: March 20, 2026</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-base p-8 prose prose-invert max-w-none"
          >
            <p className="text-viraly-muted mb-6">
              Welcome to Viraly ("we", "us", "our"). By accessing or using our AI-powered social media automation platform at viraly.ca, you agree to be bound by these Terms of Service.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">1. ACCEPTANCE OF TERMS</h2>
            <p className="text-viraly-light mb-4">
              By creating an account or using Viraly, you confirm that you:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Are at least 18 years of age</li>
              <li>Have the legal capacity to enter into this agreement</li>
              <li>Will comply with all applicable laws and regulations</li>
              <li>Accept our Privacy Policy</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">2. SERVICE DESCRIPTION</h2>
            <p className="text-viraly-light mb-4">
              Viraly provides:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>AI-powered content generation for social media posts, videos, ads, and emails</li>
              <li>Automated posting to Facebook, Instagram, TikTok, Pinterest, LinkedIn, YouTube, and X (Twitter)</li>
              <li>Content scheduling and calendar management</li>
              <li>Brand learning from URLs - automatic extraction of brand voice and audience</li>
              <li>Personal branding features - headshot and logo overlay on images</li>
              <li>Viral score prediction and analytics</li>
              <li>AI video generation with audio script editing</li>
              <li>Ad creative generation for Facebook, Instagram, and Google</li>
              <li>Email content generation for newsletters and promotions</li>
              <li>Swipe-based content approval interface</li>
              <li>Voice-to-text input using AI transcription</li>
              <li><strong>500 FREE design templates</strong> included with every plan across 20 categories (General, Property, Product, Service, Event, Job, Restaurant, Automotive, Fitness, Travel, Education, Pet, Rental, Inspiration, Testimonials, Marketing, Business, E-commerce, Real Estate, Social Proof)</li>
              <li><strong>Multi-Image Collage</strong> - combine up to 9 images into professional Open House flyers, grid layouts, and hero posts with custom text styling</li>
              <li><strong>Custom Text Styling</strong> - customize fonts, colors, sizes, and backgrounds for text overlays on your posts</li>
              <li><strong>Bulk Content Creation</strong> - schedule up to 90 days of content with unique AI variations for each day</li>
              <li><strong>Auto-Pilot Content Creation</strong> - automated content generation, scheduling, and publishing based on your brand profile and industry trends</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">2.1 Auto-Pilot Feature Terms</h3>
            <p className="text-viraly-muted mb-4">
              When you enable Auto-Pilot, you authorize Viraly to automatically create and publish content to your connected social media accounts based on your brand profile and selected preferences. By using Auto-Pilot, you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>You have reviewed and approved your Auto-Pilot settings before activation</li>
              <li>Content will be generated and posted automatically according to your configured schedule</li>
              <li>If you select "Auto-Approve" mode, posts will publish without individual review</li>
              <li>You are responsible for monitoring your Auto-Pilot activity and adjusting settings as needed</li>
              <li>Viraly is not liable for any content published under Auto-Pilot mode that you did not manually review</li>
              <li>You can pause or disable Auto-Pilot at any time from your dashboard</li>
              <li>Credits will be consumed for Auto-Pilot generated content as per your plan</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">2.2 Data Retention & Storage</h3>
            <p className="text-viraly-muted mb-4">
              We retain user-generated content (images, videos) for a period of 90 days from creation date. After this period, content is automatically purged from our servers. Users should download and save any content they wish to keep permanently. This policy helps us maintain optimal performance while respecting your storage needs.
            </p>
            <p className="text-viraly-muted mb-4">
              <strong>Important:</strong> Financial records and transaction data are retained for 7 years in compliance with Canadian tax regulations. This includes payment history, invoices, and subscription records.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">2.2 Engagement Analytics</h3>
            <p className="text-viraly-muted mb-4">
              When you connect social accounts, we request permission to read engagement data (likes, comments, shares) to provide performance analytics. This data is synced every 6 hours for published posts and helps us provide AI-powered insights to improve your content strategy.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">3. USER ACCOUNTS</h2>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">3.1 Account Creation</h3>
            <p className="text-viraly-muted mb-4">
              You must provide accurate, complete, and current information when creating your account. You are responsible for maintaining the security of your account credentials.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">3.2 Account Responsibility</h3>
            <p className="text-viraly-muted mb-4">
              You are solely responsible for all activities that occur under your account. Notify us immediately of any unauthorized access or security breach.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">4. SUBSCRIPTION AND PAYMENTS</h2>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.1 Plans and Pricing</h3>
            <p className="text-viraly-muted mb-4">
              We offer various subscription plans with different features and post limits. Prices are displayed on our pricing page and are subject to change with notice.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.2 Video Credits System</h3>
            <p className="text-viraly-muted mb-4">
              Video generation uses a credit-based system. Credits are consumed based on video duration and quality:
            </p>
            <p className="text-viraly-light font-semibold mb-2">Growth Plan (4 credits/month, 720p only):</p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>10-second video: 1 credit</li>
              <li>20-second video: 2 credits</li>
            </ul>
            <p className="text-viraly-light font-semibold mb-2">Pro Plan (8 credits/month, up to 1080p):</p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>20-second 720p video: 2 credits</li>
              <li>20-second 1080p video: 4 credits</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Growth Plan users have access to 720p quality videos only. Pro Plan users have access to both 720p and 1080p videos. Plan video credits are deducted first, followed by Pay As You Go credits.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.3 Pay As You Go Video Credit Packs (10s/20s Videos)</h3>
            <p className="text-viraly-muted mb-4">
              For additional 10s and 20s videos beyond your plan credits, you can purchase Video Credit Packs:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Quick Pack:</strong> 2 credits - $4.00 (for 2×10s or 1×20s videos)</li>
              <li><strong>Standard Pack:</strong> 4 credits - $8.00 (for 4×10s or 2×20s videos)</li>
              <li><strong>HD Pack:</strong> 12 credits - $22.50 (for 20s videos only: 6×20s-720p or 3×20s-1080p)</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              <strong>Note:</strong> HD Pack is designed for 20s videos only. For 30s+ videos, please purchase Extended Video Packs.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.4 Extended Video Packs (30s-60s Videos)</h3>
            <p className="text-viraly-muted mb-4">
              For videos longer than 20 seconds (30s, 40s, 50s, 60s), customers must purchase Extended Video Packs separately:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>720p Standard:</strong> 30s: $6 | 40s: $8 | 50s: $10 | 60s: $12</li>
              <li><strong>1080p HD (Pro only):</strong> 30s: $25 | 40s: $32 | 50s: $40 | 60s: $48</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Extended Video Packs are one-time purchases and cannot be regenerated.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.5 Regeneration Credits</h3>
            <p className="text-viraly-muted mb-4">
              Regeneration credits allow you to recreate content you're not satisfied with. Credits consumed vary by content type:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Image posts:</strong> 1 regen credit</li>
              <li><strong>10s 720p videos:</strong> 3 regen credits</li>
              <li><strong>20s 720p videos:</strong> 6 regen credits</li>
              <li><strong>20s 1080p videos:</strong> 16 regen credits</li>
              <li><strong>Extended videos (30s+):</strong> Cannot be regenerated (one-time purchase)</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Each post can only be regenerated once. Pay As You Go regen packs: Basic (5 credits, $1.99), Standard (15 credits, $4.99), Bulk (30 credits, $7.99).
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.6 Billing</h3>
            <p className="text-viraly-muted mb-4">
              Subscriptions are billed monthly in advance. Canadian customers will be charged applicable taxes (GST/HST/PST/QST based on province). Payments are processed securely through PayPal.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.7 Pay As You Go Credits Expiry</h3>
            <p className="text-viraly-muted mb-4">
              Pay-as-you-go credits (posts, video credits, and Extended Video Packs) have a validity period of 90 days from purchase date unless otherwise specified. Expired credits cannot be refunded, extended, or transferred.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.8 Refund Policy</h3>
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 mb-4">
              <p className="text-red-400 font-semibold mb-2">⚠️ IMPORTANT REFUND POLICY</p>
            </div>
            <p className="text-viraly-muted mb-4">
              We offer a <strong>7-day satisfaction guarantee</strong> for first-time subscribers only. After this period:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Subscriptions are non-refundable</strong> after the 7-day trial period</li>
              <li><strong>Unused credits do not roll over</strong> to the next billing period</li>
              <li><strong>Pay As You Go purchases are non-refundable</strong> once processed</li>
              <li><strong>Extended Video Pack purchases are final</strong> and non-refundable</li>
              <li><strong>Partial month subscriptions are not refunded</strong> upon cancellation</li>
              <li><strong>Chargebacks/disputes filed without prior contact</strong> may result in immediate account termination</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              If you experience technical issues, please contact care@viraly.ca before initiating any payment disputes. We are committed to resolving issues fairly but reserve the right to pursue all legal remedies for fraudulent chargebacks.
            </p>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.9 Loyalty Program</h3>
            <p className="text-viraly-muted mb-4">
              Our Rolling 12-Month Loyalty Program rewards your spending with permanent discounts:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Bronze (5% off)</strong>: Spend $100+ in the last 12 months</li>
              <li><strong>Silver (10% off)</strong>: Spend $300+ in the last 12 months</li>
              <li><strong>Gold (15% off)</strong>: Spend $750+ in the last 12 months</li>
              <li><strong>Platinum (20% off)</strong>: Spend $1,500+ in the last 12 months</li>
              <li><strong>Diamond (25% off)</strong>: Spend $3,000+ in the last 12 months</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Tier status is recalculated based on your rolling 12-month spend. Discounts apply to ALL purchases and can stack with promotions and coupons (up to a maximum discount cap set by Viraly, typically 50%). Tier thresholds and discount percentages may be adjusted at our discretion with 30 days notice.
            </p>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.10 Referral Program</h3>
            <p className="text-viraly-muted mb-4">
              Earn rewards by referring friends to Viraly:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Share your unique referral code or QR code</li>
              <li>When your friend signs up and makes their first purchase, you receive free post credits</li>
              <li>Your friend receives a discount on their first purchase</li>
              <li>Referral rewards are subject to verification and may be revoked for abuse</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Self-referrals, fake accounts, or referral fraud will result in immediate account termination and forfeiture of all rewards. We reserve the right to modify or discontinue the referral program at any time.
            </p>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.11 Gift a Pack</h3>
            <p className="text-viraly-muted mb-4">
              Send credits to friends, family, or colleagues with our Gift a Pack feature:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Purchase any credit pack and send it to someone via email</li>
              <li>Recipients receive an email with a unique gift code and redemption instructions</li>
              <li>Gift codes are valid for 90 days from the date of purchase</li>
              <li>Credits are added to the recipient's account upon redemption</li>
              <li>New users can create an account during redemption; existing users can redeem instantly</li>
              <li>Gifts cannot be refunded once purchased or redeemed</li>
              <li>You can include a personal message with your gift</li>
            </ul>

            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">4.12 Influencer Program</h3>
            <p className="text-viraly-muted mb-4">
              Top referrers may qualify for our Influencer Program and earn monetary commissions:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Eligibility requires meeting referral thresholds set by Viraly</li>
              <li>Influencers earn a commission (typically 10%) on purchases made by their referred users</li>
              <li>Commissions are tracked and paid out via PayPal or e-Transfer upon request</li>
              <li>Minimum payout thresholds apply before withdrawal requests can be made</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              <strong>Influencer Publicity Consent:</strong> By participating in our Influencer Program, you grant Viraly permission to display your name (first name and last initial), general location (city/country), total earnings, projected monthly average earnings, and referral count on our website, marketing materials, and promotional content. This information helps demonstrate the success of our community and inspires others. <strong>Monthly average earnings displayed are estimates calculated based on your activity period and may not reflect actual month-to-month variations.</strong> No other personal information will be shared. You may opt out of public display by contacting support, though this may affect your eligibility for the Influencer Program.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">5. ACCEPTABLE USE</h2>
            <p className="text-viraly-light mb-4">
              You agree NOT to use Viraly to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Create or distribute spam, harassment, or abusive content</li>
              <li>Violate intellectual property rights or plagiarize content</li>
              <li>Impersonate others or create misleading content</li>
              <li>Distribute malware, viruses, or harmful code</li>
              <li>Violate social media platform terms of service</li>
              <li>Engage in illegal activities or promote violence</li>
              <li>Create content that is defamatory, obscene, or discriminatory</li>
              <li>Circumvent usage limits or abuse the service</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">6. CONTENT OWNERSHIP</h2>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">6.1 Your Content</h3>
            <p className="text-viraly-muted mb-4">
              You retain ownership of content you create using Viraly. By using our service, you grant us a limited license to process and display your content as necessary to provide the service.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">6.2 AI-Generated Content</h3>
            <p className="text-viraly-muted mb-4">
              Content generated by our AI tools is licensed to you for your use. You are responsible for reviewing and approving all content before publishing.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">7. AI-GENERATED CONTENT DISCLAIMER</h2>
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 mb-4">
              <p className="text-yellow-400 font-semibold mb-2">⚠️ IMPORTANT - PLEASE READ CAREFULLY</p>
            </div>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">7.1 Nature of AI Content</h3>
            <p className="text-viraly-muted mb-4">
              All content generated by Viraly, including but not limited to text captions, video scripts, translations, voiceovers, images, and advertisements, is created using artificial intelligence (AI) technology. While we strive for accuracy and quality, AI-generated content:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>May contain errors, inaccuracies, or factual mistakes</li>
              <li>May not perfectly reflect your intended message or brand voice</li>
              <li>May require human review and editing before use</li>
              <li>Should not be relied upon for legal, medical, financial, or professional advice</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">7.2 Multi-Language Translations</h3>
            <p className="text-viraly-muted mb-4">
              Viraly offers AI-powered translation into 71 languages. You acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Translations are AI-generated and may contain errors</strong></li>
              <li>Translations may not capture cultural nuances, idioms, or context accurately</li>
              <li>Translations may be grammatically incorrect or use inappropriate tone</li>
              <li>Translations should be reviewed by a native speaker before publication for critical communications</li>
              <li>We do not guarantee the accuracy of any translated content</li>
              <li>You are solely responsible for verifying translations before publishing</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">7.3 AI Voiceovers</h3>
            <p className="text-viraly-muted mb-4">
              AI-generated voiceovers in videos may have pronunciation issues, especially with proper nouns, technical terms, or languages with complex phonetics. You should review all voiceovers before publishing.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">7.4 Your Responsibility</h3>
            <p className="text-viraly-muted mb-4">
              You are solely responsible for:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Reviewing all AI-generated content before publishing</li>
              <li>Verifying the accuracy of facts, statistics, and claims</li>
              <li>Ensuring translations are appropriate for your target audience</li>
              <li>Editing content to match your brand voice and standards</li>
              <li>Obtaining necessary permissions for any factual claims</li>
              <li>Any consequences resulting from publishing AI-generated content</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">7.5 No Warranty</h3>
            <p className="text-viraly-muted mb-4">
              VIRALY PROVIDES AI-GENERATED CONTENT "AS IS" WITHOUT ANY WARRANTY OF ACCURACY, COMPLETENESS, OR FITNESS FOR A PARTICULAR PURPOSE. WE EXPRESSLY DISCLAIM ALL LIABILITY FOR ERRORS, OMISSIONS, OR INACCURACIES IN AI-GENERATED CONTENT, INCLUDING TRANSLATIONS AND VOICEOVERS.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">8. STOCK PHOTOS AND THIRD-PARTY IMAGERY</h2>
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 mb-4">
              <p className="text-blue-400 font-semibold mb-2">ℹ️ IMPORTANT - THIRD-PARTY CONTENT DISCLAIMER</p>
            </div>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">8.1 Source of Stock Images</h3>
            <p className="text-viraly-muted mb-4">
              Viraly may use stock photographs from third-party providers including but not limited to Unsplash, Pexels, and similar stock photo services. These images are used under their respective licenses (typically Unsplash License or Pexels License) which permit free use for commercial purposes.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">8.2 No Representation or Endorsement</h3>
            <p className="text-viraly-muted mb-4">
              <strong>YOU ACKNOWLEDGE AND AGREE THAT:</strong>
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Viraly does not own, represent, or endorse</strong> any individuals, businesses, locations, landmarks, cultural symbols, religious sites, or other subjects depicted in stock photographs</li>
              <li><strong>Stock images are illustrative only</strong> and may not accurately represent specific people, places, products, or services</li>
              <li><strong>Individuals appearing in stock photos are models</strong> and their appearance does not imply endorsement of any product, service, or viewpoint</li>
              <li><strong>Landmarks, religious sites, cultural symbols, and locations</strong> depicted in stock photos are used for illustrative purposes only and do not represent any official association, endorsement, or affiliation</li>
              <li><strong>We do not claim ownership or rights</strong> over the subjects depicted in third-party stock images</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">8.3 User Responsibility for Image Selection</h3>
            <p className="text-viraly-muted mb-4">
              When you select or approve stock images for your content, you assume full responsibility for:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Ensuring the images are appropriate for your intended use and audience</li>
              <li>Verifying that images do not misrepresent your business, products, or services</li>
              <li>Respecting cultural, religious, and ethnic sensitivities in your image selection</li>
              <li>Ensuring images comply with applicable laws and regulations in your jurisdiction</li>
              <li>Any consequences arising from the use of stock images in your published content</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">8.4 Sensitive Content Categories</h3>
            <p className="text-viraly-muted mb-4">
              Special care must be taken when using images depicting:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Religious sites and symbols:</strong> Churches, mosques, temples, religious ceremonies, sacred objects</li>
              <li><strong>Cultural elements:</strong> Traditional clothing, cultural practices, ethnic representations</li>
              <li><strong>Historical landmarks:</strong> National monuments, heritage sites, historically significant locations</li>
              <li><strong>People and identifiable individuals:</strong> Even stock models should be used respectfully</li>
              <li><strong>Geographical locations:</strong> Country flags, national symbols, regional identifiers</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              <strong>Viraly expressly disclaims any responsibility</strong> for offense, misrepresentation, or misuse arising from user-selected images in these sensitive categories. You are solely responsible for ensuring appropriate and respectful use.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">8.5 No Guarantee of Availability</h3>
            <p className="text-viraly-muted mb-4">
              Stock images are provided by third-party services and their availability, licensing terms, or content may change without notice. We do not guarantee continuous availability of any specific stock image.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">9. USER CONTENT AND PUBLISHING AUTHORIZATION</h2>
            <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 mb-4">
              <p className="text-purple-400 font-semibold mb-2">📝 PUBLISHING CONSENT AND AUTHORIZATION</p>
            </div>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">9.1 Authorization to Post</h3>
            <p className="text-viraly-muted mb-4">
              By using Viraly to create and schedule content, you expressly authorize us to:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Generate content (text, images, videos) based on your inputs and brand profile</li>
              <li>Post content to your connected social media accounts on your behalf</li>
              <li>Schedule and automatically publish content at times you specify</li>
              <li>Apply your brand elements (logo, headshot, colors) to generated content</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">9.2 Your Approval Responsibility</h3>
            <p className="text-viraly-muted mb-4">
              <strong>BY CLICKING "GENERATE", "POST", "SCHEDULE", OR ANY SIMILAR ACTION, YOU CONFIRM THAT:</strong>
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>You have reviewed and approved all content including captions, hashtags, images, and videos</li>
              <li>You authorize Viraly to publish this content on your connected social media platforms</li>
              <li>You take full responsibility for the content published under your accounts</li>
              <li>You have the right to use any brand names, trademarks, or intellectual property mentioned</li>
              <li>The content does not violate any laws, regulations, or third-party rights</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">9.3 Views and Opinions Disclaimer</h3>
            <p className="text-viraly-muted mb-4">
              <strong>VIRALY DOES NOT ENDORSE, SUPPORT, OR REPRESENT:</strong>
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Any views, opinions, statements, or claims expressed in user-generated or AI-assisted content</li>
              <li>Any products, services, or businesses promoted through our platform</li>
              <li>Any political, religious, social, or cultural viewpoints expressed in published content</li>
              <li>The accuracy or truthfulness of any claims made in user content</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              All content published through Viraly represents the views and opinions of the user who created and approved it, NOT the views of Viraly or its operators.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">9.4 Indemnification for Published Content</h3>
            <p className="text-viraly-muted mb-4">
              You agree to indemnify, defend, and hold harmless Viraly and its operators from any and all claims, damages, losses, costs, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Content you publish or authorize for publication through our service</li>
              <li>Any misrepresentation in your published content</li>
              <li>Infringement of third-party rights through your content</li>
              <li>Violations of applicable laws or platform policies</li>
              <li>Claims related to stock images you selected and approved for use</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">10. THIRD-PARTY PLATFORMS</h2>
            <p className="text-viraly-muted mb-4">
              Viraly integrates with Facebook, Instagram, TikTok, Pinterest, LinkedIn, YouTube, and X (Twitter). Your use of these platforms is subject to their respective terms of service. We are not responsible for actions taken by these platforms, including account suspensions or content removal.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">11. AUTO-POSTING AND SCHEDULING CONSENT</h2>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">11.1 Automatic Publishing</h3>
            <p className="text-viraly-muted mb-4">
              By scheduling content through Viraly, you expressly consent to automatic publishing of that content to your connected social media accounts at the scheduled time. Once content is added to your scheduled queue, it will be automatically posted without requiring additional confirmation.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">11.2 Your Responsibility</h3>
            <p className="text-viraly-muted mb-4">
              You are solely responsible for reviewing all scheduled content before the posting time. You may edit or delete any scheduled post at any time before it is published. We recommend reviewing your content queue regularly.
            </p>
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">11.3 Content Review</h3>
            <p className="text-viraly-muted mb-4">
              While we provide AI-powered content generation, you maintain full control and responsibility over what gets published. By using the scheduling feature, you acknowledge that you have reviewed and approved the content for publication on your behalf.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">12. LIMITATION OF LIABILITY</h2>
            <p className="text-viraly-muted mb-4">
              To the maximum extent permitted by law, Viraly and its operators shall not be liable for:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Indirect, incidental, or consequential damages</li>
              <li>Loss of profits, data, or business opportunities</li>
              <li>Service interruptions or technical failures</li>
              <li>Actions of third-party platforms</li>
              <li>Content posted by users</li>
              <li>Errors or inaccuracies in AI-generated content or translations</li>
              <li>Any damages resulting from translated or AI-generated content</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">13. INDEMNIFICATION</h2>
            <p className="text-viraly-muted mb-4">
              You agree to indemnify and hold harmless Viraly, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the service, violation of these terms, or publication of AI-generated content including translations.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">14. TERMINATION</h2>
            <p className="text-viraly-muted mb-4">
              We may suspend or terminate your account at our discretion for violations of these terms or for any other reason with notice. You may cancel your subscription at any time through your account settings.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">15. CHANGES TO TERMS</h2>
            <p className="text-viraly-muted mb-4">
              We may update these Terms of Service periodically. We will notify you of significant changes via email or platform notification. Continued use after changes constitutes acceptance.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">16. B2B ENTERPRISE AND MERCHANT TERMS</h2>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.1 Enterprise Accounts</h3>
            <p className="text-viraly-muted mb-4">
              Enterprises that subscribe to Viraly's B2B wholesale plans (Builder, Scaler, or Infinite) are subject to the Enterprise Master Service Agreement, available at viraly.ca/downloads/legal/. Enterprise accounts may:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Manage credit pools and allocate credits to Merchants</li>
              <li>Access API integrations for custom implementations</li>
              <li>Create and manage Merchant accounts under their organization</li>
              <li>Receive priority support via Vira On-Call</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.2 Merchant Accounts</h3>
            <p className="text-viraly-muted mb-4">
              Merchants are end-users onboarded by Enterprises. Merchants acknowledge that:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li>Their relationship is primarily with the Enterprise, not Viraly</li>
              <li>Credit allocation and access are controlled by the Enterprise</li>
              <li>They have no direct billing relationship with Viraly</li>
              <li>Support requests should first be directed to their Enterprise</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.3 Partner Program</h3>
            <p className="text-viraly-muted mb-4">
              Partners who refer Enterprise clients are subject to the Partner Agreement available at viraly.ca/downloads/legal/. Partner commissions are subject to the terms therein.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">16.4 Legal Documents</h3>
            <p className="text-viraly-muted mb-4">
              The following legal documents govern B2B relationships and are available for download:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong className="text-viraly-light">Partner Agreement:</strong> Terms for referral partners</li>
              <li><strong className="text-viraly-light">Enterprise Agreement:</strong> Master service agreement for Enterprise customers</li>
              <li><strong className="text-viraly-light">NDA:</strong> Non-disclosure agreement for confidential discussions</li>
              <li><strong className="text-viraly-light">DPA:</strong> Data Processing Agreement for GDPR/PIPEDA compliance</li>
              <li><strong className="text-viraly-light">Content Liability Waiver:</strong> AI content disclaimer and indemnification</li>
            </ul>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">17. GOVERNING LAW</h2>
            <p className="text-viraly-muted mb-4">
              These terms are governed by the laws of the Province of Ontario, Canada, and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">17. GOVERNING LAW</h2>
            <p className="text-viraly-muted mb-4">
              These terms are governed by the laws of the Province of Ontario, Canada, and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">18. INTERNATIONAL USERS AND GLOBAL COMPLIANCE</h2>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">18.1 Global Availability</h3>
            <p className="text-viraly-muted mb-4">
              Viraly is available to users in 195+ countries. By using our service from outside Canada, you consent to the transfer of your data to Canada for processing. We comply with applicable local laws where required.
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">18.2 European Union Users (GDPR)</h3>
            <p className="text-viraly-muted mb-4">
              For users in the European Economic Area, we process data under the following legal bases:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>Contract:</strong> Processing necessary to provide our services</li>
              <li><strong>Consent:</strong> For marketing communications and optional features</li>
              <li><strong>Legitimate Interest:</strong> For fraud prevention and service improvement</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              EU users have additional rights including data portability, restriction of processing, and the right to object. Data transfers outside the EEA are protected by Standard Contractual Clauses (SCCs).
            </p>
            
            <h3 className="text-lg font-semibold text-viraly-light mt-4 mb-2">18.3 Content Moderation by Region</h3>
            <p className="text-viraly-muted mb-4">
              Our AI content moderation adapts to regional requirements:
            </p>
            <ul className="list-disc list-inside text-viraly-muted space-y-2 mb-4">
              <li><strong>EU/UK:</strong> GDPR-compliant processing, NetzDG compliance for German users</li>
              <li><strong>Middle East:</strong> Cultural sensitivity filters for religious and political content</li>
              <li><strong>Asia-Pacific:</strong> Compliance with local content regulations where applicable</li>
              <li><strong>Americas:</strong> COPPA compliance for US users, LGPD for Brazil</li>
            </ul>
            <p className="text-viraly-muted mb-4">
              Users are ultimately responsible for ensuring their content complies with local laws in their jurisdiction and target markets.
            </p>

            <h2 className="font-heading text-xl font-bold text-white mt-8 mb-4">19. CONTACT US</h2>
            <p className="text-viraly-muted mb-4">
              For questions about these Terms of Service:
            </p>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
              <p className="text-viraly-light font-medium">Viraly</p>
              <p className="text-viraly-muted">Email: <a href="mailto:care@viraly.ca" className="text-viraly-orange hover:underline">care@viraly.ca</a></p>
              <p className="text-viraly-muted">Website: <a href="https://viraly.ca" className="text-viraly-orange hover:underline">viraly.ca</a></p>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-viraly-muted text-sm">
                By using Viraly, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Viraly" className="h-6 w-6" />
            <span className="font-heading font-bold">Viraly</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-viraly-muted">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <a href="mailto:care@viraly.ca" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-viraly-muted text-sm">© 2026 Viraly</p>
        </div>
      </footer>
    </div>
  );
}
